import { z } from 'zod';

export const CONTACT_CHANNELS = ['PHONE', 'EMAIL'] as const;

export const ContactChannelSchema = z.enum(CONTACT_CHANNELS);

export type ContactChannel = z.infer<typeof ContactChannelSchema>;

export const CONTACT_COMMERCIAL_TYPES = ['SALESPERSON', 'REPRESENTATIVE', 'OTHER'] as const;

export const ContactCommercialTypeSchema = z.enum(CONTACT_COMMERCIAL_TYPES);

export type ContactCommercialType = z.infer<typeof ContactCommercialTypeSchema>;

export const ContactOwnerReadModelSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('PARTY'),
    partyId: z.uuid(),
  }),
  z.object({
    kind: z.literal('PARTY_RESPONSIBLE'),
    partyResponsibleId: z.uuid(),
  }),
]);

export type ContactOwnerReadModel = z.infer<typeof ContactOwnerReadModelSchema>;

export const ContactChannelEntrySchema = z.object({
  channel: ContactChannelSchema,
  value: z.string(),
  isWhatsapp: z.boolean(),
});

export const ContactResponseSchema = z.object({
  id: z.uuid(),
  owner: ContactOwnerReadModelSchema,
  name: z.string().nullable(),
  commercialType: ContactCommercialTypeSchema.nullable(),
  commercialTypeLabel: z.string().nullable(),
  channels: z.array(ContactChannelEntrySchema),
  isPrimary: z.boolean(),
  active: z.boolean(),
  notes: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type ContactResponse = z.infer<typeof ContactResponseSchema>;

const ContactChannelsBodySchema = z
  .array(
    z.object({
      channel: ContactChannelSchema,
      value: z.string().trim().min(1),
      isWhatsapp: z.boolean().optional(),
    }),
  )
  .min(1);

function refineCommercialTypeLabel(
  data: {
    commercialType: ContactCommercialType | null;
    commercialTypeLabel?: string | null | undefined;
  },
  ctx: z.RefinementCtx,
): void {
  if (data.commercialType === null) {
    if (data.commercialTypeLabel != null && data.commercialTypeLabel.trim() !== '') {
      ctx.addIssue({
        code: 'custom',
        path: ['commercialTypeLabel'],
        message: 'commercialTypeLabel must be null when commercialType is null',
      });
    }
    return;
  }

  if (data.commercialType === 'OTHER') {
    const label = data.commercialTypeLabel?.trim() ?? '';
    if (!label) {
      ctx.addIssue({
        code: 'custom',
        path: ['commercialTypeLabel'],
        message: 'commercialTypeLabel is required when commercialType is OTHER',
      });
    }
    return;
  }

  if (data.commercialTypeLabel != null && data.commercialTypeLabel.trim() !== '') {
    ctx.addIssue({
      code: 'custom',
      path: ['commercialTypeLabel'],
      message: 'commercialTypeLabel must be null when commercialType is not OTHER',
    });
  }
}

export const CreatePartyContactBodySchema = z
  .object({
    partyResponsibleId: z.uuid().optional(),
    name: z.string().trim().min(1).nullable(),
    commercialType: ContactCommercialTypeSchema.nullable(),
    commercialTypeLabel: z.string().trim().min(1).nullable().optional(),
    channels: ContactChannelsBodySchema,
    isPrimary: z.boolean().optional(),
    notes: z.string().trim().nullable().optional(),
  })
  .superRefine(refineCommercialTypeLabel);

export type CreatePartyContactBody = z.infer<typeof CreatePartyContactBodySchema>;

export const UpdateContactBodySchema = z
  .object({
    name: z.string().trim().min(1).nullable(),
    commercialType: ContactCommercialTypeSchema.nullable(),
    commercialTypeLabel: z.string().trim().min(1).nullable().optional(),
    channels: ContactChannelsBodySchema,
    isPrimary: z.boolean().optional(),
    notes: z.string().trim().nullable().optional(),
    active: z.boolean().optional(),
  })
  .superRefine(refineCommercialTypeLabel);

export type UpdateContactBody = z.infer<typeof UpdateContactBodySchema>;

export const CreatePartyContactResponseSchema = z.object({
  contactId: z.uuid(),
});

export type CreatePartyContactResponse = z.infer<typeof CreatePartyContactResponseSchema>;

export const UpdateContactResponseSchema = z.object({
  contactId: z.uuid(),
  changedFields: z.array(z.string()),
});

export type UpdateContactResponse = z.infer<typeof UpdateContactResponseSchema>;

export const DeleteContactResponseSchema = z.object({
  contactId: z.uuid(),
});

export type DeleteContactResponse = z.infer<typeof DeleteContactResponseSchema>;

export const ListPartyContactsResponseSchema = z.object({
  items: z.array(ContactResponseSchema),
});

export type ListPartyContactsResponse = z.infer<typeof ListPartyContactsResponseSchema>;

export const GetContactByIdResponseSchema = z.object({
  contact: ContactResponseSchema,
});

export type GetContactByIdResponse = z.infer<typeof GetContactByIdResponseSchema>;

export const ContactIdParamsSchema = z.object({
  contactId: z.uuid(),
});

export type ContactIdParams = z.infer<typeof ContactIdParamsSchema>;
